/**
 * NameAndAvatarTab: the "Name and Avatar" tab of the "Me" dialog.
 *
 * Name is shown as plain text and becomes an editable input on click (commits on
 * blur/Enter, reverts on Escape). Thumbnail changes (upload, pick from history,
 * clear to initials) apply immediately - there is no separate Save step.
 */
import * as React from 'react'
import { getInitials, Avatar, AvatarFallback, cn } from '@merels/ui'
import { CloseIcon } from '../../../../library/Icons/CloseIcon'
import { AVATAR_PRESETS, isPresetThumbnailValue, makePresetThumbnailValue, resolveThumbnailUrl } from '../../../../../assets/avatarPresets/avatarPresets'

const MAX_THUMBNAIL_BYTES = 1024 * 1024

export interface NameAndAvatarTabProps {
  name: string
  onNameChange: (name: string) => void
  thumbnailUrl?: string
  /** Most-recent-first, up to 10 - see @merels/room getThumbnailHistory. */
  thumbnailHistory: string[]
  onThumbnailUpload: (dataUrl: string, file: File) => void
  onThumbnailSelect: (url: string) => void
  onThumbnailDelete: (url: string) => void
  onThumbnailClear: () => void
  /** Whether Daily.co videoconferencing is enabled for the current room. */
  videoEnabled: boolean
  isSharedScreen: boolean
  onSwitchProfile?: () => void
  /**
   * Flat-list controller navigation wiring (see NameDialogUI) - all optional so this
   * component still works standalone (tests, future reuse) without a parent nav list.
   * `registerItem`/`focusedIndex` use indices already offset by `baseIndex`, i.e. this
   * component just registers its own items starting from 0 and NameDialogUI translates.
   */
  registerItem?: (index: number, el: HTMLElement | null) => void
  focusedIndex?: number
  baseIndex?: number
  showPresetGrid?: boolean
  onShowPresetGridChange?: (showPresetGrid: boolean) => void
}

export function NameAndAvatarTab({
  name,
  onNameChange,
  thumbnailUrl,
  thumbnailHistory,
  onThumbnailUpload,
  onThumbnailSelect,
  onThumbnailDelete,
  onThumbnailClear,
  videoEnabled,
  isSharedScreen,
  onSwitchProfile,
  registerItem,
  focusedIndex,
  baseIndex = 0,
  showPresetGrid: controlledShowPresetGrid,
  onShowPresetGridChange,
}: NameAndAvatarTabProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [thumbnailError, setThumbnailError] = React.useState<string | null>(null)
  const [isEditingName, setIsEditingName] = React.useState(false)
  const [nameDraft, setNameDraft] = React.useState(name)
  const [uncontrolledShowPresetGrid, setUncontrolledShowPresetGrid] = React.useState(false)
  const showPresetGrid = controlledShowPresetGrid ?? uncontrolledShowPresetGrid

  const setShowPresetGrid = (next: boolean | ((current: boolean) => boolean)) => {
    const resolved = typeof next === 'function' ? next(showPresetGrid) : next
    if (controlledShowPresetGrid === undefined) {
      setUncontrolledShowPresetGrid(resolved)
    }
    onShowPresetGridChange?.(resolved)
  }

  // Local items are registered in render order, offset by baseIndex, to participate
  // in NameDialogUI's single flat nav list - see NameDialogUI.tsx.
  let nextLocalIndex = 0
  const editNameIndex = nextLocalIndex++
  // File upload only makes sense on the device you're actually using - shared-screen
  // mates pick from the built-in preset grid below instead (see AVATAR_PRESETS).
  const uploadPhotoIndex = !isSharedScreen ? nextLocalIndex++ : -1
  const useInitialsIndex = thumbnailUrl ? nextLocalIndex++ : -1
  const switchProfileIndex = isSharedScreen && onSwitchProfile ? nextLocalIndex++ : -1
  const presetStackIndex = nextLocalIndex++
  const presetGridStartIndex = nextLocalIndex
  nextLocalIndex += AVATAR_PRESETS.length
  const isItemFocused = (localIndex: number) => registerItem !== undefined && focusedIndex === baseIndex + localIndex

  const startEditingName = () => {
    setNameDraft(name)
    setIsEditingName(true)
  }

  const commitName = () => {
    const trimmed = nameDraft.trim()
    if (trimmed && trimmed !== name) onNameChange(trimmed)
    setIsEditingName(false)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_THUMBNAIL_BYTES) {
      setThumbnailError('Image must be smaller than 1MB')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      if (typeof event.target?.result === 'string') {
        onThumbnailUpload(event.target.result, file)
        setThumbnailError(null)
      }
    }
    reader.onerror = () => {
      setThumbnailError('Error reading file')
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="flex flex-col gap-4">
      {isEditingName ? (
        <div
          className={cn(
            'self-start flex min-h-12 w-[240px] max-w-full items-center rounded-lg border border-basic bg-background px-3 py-3',
            isItemFocused(editNameIndex) && 'ring-2 ring-accent ring-offset-2 ring-offset-background'
          )}
        >
          <input
            ref={(el) => registerItem?.(baseIndex + editNameIndex, el)}
            type="text"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName()
              if (e.key === 'Escape') setIsEditingName(false)
            }}
            autoFocus
            className="block w-full appearance-none border-0 bg-transparent p-0 text-lg font-semibold leading-none text-primary shadow-none outline-none ring-0 focus:outline-none focus:ring-0"
          />
        </div>
      ) : (
        <div className="self-start p-1">
          <button
            ref={(el) => registerItem?.(baseIndex + editNameIndex, el)}
            type="button"
            onClick={startEditingName}
            className={cn(
              'block rounded-md px-2 py-1 text-left text-lg font-semibold text-primary transition-colors hover:text-accent cursor-pointer focus:outline-none',
              isItemFocused(editNameIndex) && 'ring-2 ring-accent'
            )}
            title="Click to edit name"
          >
            {name || 'Unnamed'}
          </button>
        </div>
      )}

      {thumbnailError && (
        <div className="text-xs text-danger bg-danger-bg/10 border border-danger-bg/25 p-2 rounded">
          {thumbnailError}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Avatar className="h-16 w-16 rounded-md flex-shrink-0">
          {thumbnailUrl ? (
            <img src={resolveThumbnailUrl(thumbnailUrl)} alt="" className="h-full w-full object-cover rounded-md" />
          ) : (
            <AvatarFallback className="rounded-md bg-accent/20 text-primary text-lg font-bold">
              {getInitials(name)}
            </AvatarFallback>
          )}
        </Avatar>
        <div className="flex flex-col gap-1.5">
          {!isSharedScreen && (
            <button
              ref={(el) => registerItem?.(baseIndex + uploadPhotoIndex, el)}
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'text-xs font-bold border-2 border-dashed border-muted/30 hover:border-accent hover:bg-surface-hover/10 rounded px-3 py-1.5 text-muted hover:text-primary transition-all cursor-pointer focus:outline-none',
                isItemFocused(uploadPhotoIndex) && 'ring-2 ring-accent'
              )}
            >
              Upload Photo
            </button>
          )}
          {thumbnailUrl && (
            <button
              ref={(el) => registerItem?.(baseIndex + useInitialsIndex, el)}
              type="button"
              onClick={onThumbnailClear}
              className={cn(
                'text-xs font-semibold text-muted hover:text-primary transition-colors cursor-pointer focus:outline-none text-left',
                isItemFocused(useInitialsIndex) && 'ring-2 ring-accent rounded'
              )}
            >
              Use initials instead
            </button>
          )}
          {isSharedScreen && onSwitchProfile && (
            <button
              ref={(el) => registerItem?.(baseIndex + switchProfileIndex, el)}
              type="button"
              onClick={onSwitchProfile}
              className={cn(
                'text-xs font-semibold text-muted hover:text-primary transition-colors cursor-pointer focus:outline-none text-left',
                isItemFocused(switchProfileIndex) && 'ring-2 ring-accent rounded'
              )}
            >
              Switch Profile
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>

      <div>
        <div className="text-xs font-bold text-muted uppercase tracking-wider mb-1.5">Choose an avatar</div>
        <button
          ref={(el) => registerItem?.(baseIndex + presetStackIndex, el)}
          type="button"
          onClick={() => setShowPresetGrid((current) => !current)}
          className={cn(
            'flex w-full items-center justify-between rounded-lg border border-muted/20 bg-surface-hover/10 px-3 py-2 text-left transition-colors hover:border-accent/50 hover:bg-surface-hover/20 focus:outline-none',
            isItemFocused(presetStackIndex) && 'ring-2 ring-accent'
          )}
          aria-expanded={showPresetGrid}
        >
          <div className="flex items-center gap-3">
            <div className="relative h-10 w-14 flex-shrink-0">
              {AVATAR_PRESETS.slice(0, 3).map((preset, index) => (
                <div
                  key={preset.id}
                  className="absolute top-0 h-10 w-10 overflow-hidden rounded-md border border-surface bg-surface shadow-sm"
                  style={{ left: `${index * 12}px`, zIndex: 3 - index }}
                >
                  <img src={preset.url} alt="" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-primary">Built-in avatars</div>
              <div className="text-xs text-muted">
                {showPresetGrid ? 'Hide options' : 'Show all options'}
              </div>
            </div>
          </div>
          <div className="text-xs font-semibold text-muted">{showPresetGrid ? 'Hide' : 'Choose'}</div>
        </button>

        {showPresetGrid && (
          <div className="mt-2 grid grid-cols-5 gap-2">
            {AVATAR_PRESETS.map((preset, i) => {
              const presetIndex = presetGridStartIndex + i
              const presetValue = makePresetThumbnailValue(preset.id)
              const isSelected = thumbnailUrl === presetValue
              return (
                <button
                  key={preset.id}
                  ref={(el) => registerItem?.(baseIndex + presetIndex, el)}
                  type="button"
                  onClick={() => onThumbnailSelect(presetValue)}
                  className={cn(
                    'block aspect-square rounded-md overflow-hidden border-2 cursor-pointer focus:outline-none transition-colors',
                    isSelected ? 'border-accent' : 'border-transparent hover:border-basic',
                    isItemFocused(presetIndex) && 'ring-2 ring-accent'
                  )}
                  title={preset.id}
                >
                  <img src={preset.url} alt={preset.id} className="h-full w-full object-cover" />
                </button>
              )
            })}
          </div>
        )}
      </div>

      {(() => {
        const otherHistory = thumbnailHistory.filter((url) => url !== thumbnailUrl && !isPresetThumbnailValue(url))
        if (otherHistory.length === 0) return null
        return (
          <div>
            <div className="text-xs font-bold text-muted uppercase tracking-wider mb-1.5">Previously uploaded</div>
            <div className="flex flex-wrap gap-2">
              {otherHistory.map((url) => {
                const historyIndex = nextLocalIndex++
                return (
                <div key={url} className="relative group">
                  <button
                    ref={(el) => registerItem?.(baseIndex + historyIndex, el)}
                    type="button"
                    onClick={() => onThumbnailSelect(url)}
                    className={cn(
                      'block h-10 w-10 rounded-md overflow-hidden border-2 border-transparent hover:border-basic cursor-pointer focus:outline-none transition-colors',
                      isItemFocused(historyIndex) && 'ring-2 ring-accent'
                    )}
                    title="Use this photo"
                  >
                    <img src={resolveThumbnailUrl(url)} alt="" className="h-full w-full object-cover" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onThumbnailDelete(url)
                    }}
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 flex items-center justify-center rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer focus:outline-none focus:opacity-100"
                    title="Delete this photo"
                  >
                    <CloseIcon width={8} height={8} />
                  </button>
                </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {videoEnabled && (
        <p className="text-xs text-muted">Your photo will be used when your camera is off.</p>
      )}
    </div>
  )
}
